import { AREA_CODES_BY_COUNTRY_CODE } from "./areaCodes";
import { getPhoneCountryCode, getPrettyPhone, stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";

export { normalizePhoneForWhatsApp } from "./normalizePhoneForWhatsApp";
export const isPhoneNumber = (input: string) => {
  const digits = stripNumberStringToOnlyNumbers(input);
  return digits.length >= 5 && digits.length <= 15;
};

/**
 * Helper: Adds country code to a number if it doesn't have one
 */
const addCountryCodeIfNeeded = (userNumber: string, targetNumber: string): string => {
  if (targetNumber.startsWith("+")) return targetNumber;

  const cleanUserNumber = stripNumberStringToOnlyNumbers(userNumber);
  const countryCode = getPhoneCountryCode(cleanUserNumber);

  return countryCode ? `+${countryCode}${targetNumber}` : targetNumber;
};

const removeCountryCodePrefix = (fullNumber: string, countryCode: string): string => {
  return fullNumber.replace(`+${countryCode} `, "");
};

export const getOutgoingNumberWithRegionCode = (userNumber: string, outgoingNumber: string) => {
  const fullNumber = addCountryCodeIfNeeded(userNumber, outgoingNumber);
  return getPrettyPhone(fullNumber);
};

export const getFullNumberForActiveCallScreen = (userNumber: string, outgoingNumber: string) => {
  const fullNumber = addCountryCodeIfNeeded(userNumber, outgoingNumber);
  const prettyNumber = getPrettyPhone(fullNumber);

  const countryCode = getPhoneCountryCode(stripNumberStringToOnlyNumbers(userNumber)) ?? "";
  return removeCountryCodePrefix(prettyNumber, countryCode);
};

export const getFullNumberForOutgoingCall = (userNumber: string, outgoingNumber: string) => {
  return addCountryCodeIfNeeded(userNumber, outgoingNumber);
};

/**
 * Detects country code with special handling for Brazilian domestic numbers.
 * Brazilian numbers with 11 digits starting with Brazilian area codes should not be treated
 * as having international country codes (e.g. 71991599095 should not be treated as +7 Russia)
 */
export const getPhoneCountryCodeWithBrazilContext = (value: string) => {
  if (value.length === 11 && AREA_CODES_BY_COUNTRY_CODE["55"]?.some((code) => value.startsWith(code))) {
    return "55";
  }

  return getPhoneCountryCode(value);
};

export const getPhoneAreaCode = (country_code: string, phone: string) => {
  if (!AREA_CODES_BY_COUNTRY_CODE[country_code]) {
    return null;
  }
  const shortPhone = phone.substring(country_code.length);
  return AREA_CODES_BY_COUNTRY_CODE[country_code].find((code) => shortPhone.startsWith(code));
};

export const removeStartsWith = (inputString: string | undefined, prefixes: string[]) => {
  if (!inputString || prefixes.length === 0) {
    return inputString;
  }
  const regex = new RegExp(`^(${prefixes.join("|")})`);

  return inputString.replace(regex, "");
};
